import { IconButton } from "@/components/ui";
import { Back } from "@/icons";
import { cn } from "@/lib/cn";

/**
 * Geometry of the fixed top bar on overlay screens (category and item).
 * `inset-x-0` is required: with `position: fixed` and auto left/right, `mx-auto`
 * alone does not center. `px-3` keeps the button flush left.
 */
export const OVERLAY_TOP_BAR =
  "fixed inset-x-0 top-0 mx-auto flex h-header w-full max-w-shell items-center gap-3 px-3";

interface BackButtonProps {
  onClick: () => void;
  /** Accessible name: "Back" on category, "Close item details" on item. */
  label: string;
  /** `true` (default): dark glass circle over a photo; `false`: card disc with a primary chevron over a frosted bar. */
  onImage?: boolean;
  className?: string;
  "data-testid"?: string;
}

/**
 * Back button on overlay screens: a glass circle over the hero photo, a card disc with a primary chevron
 * once the frosted bar takes over (`onImage={false}`); same box, so the swap never moves it.
 */
export function BackButton({ onClick, label, onImage = true, className, ...rest }: BackButtonProps) {
  return (
    <IconButton
      {...rest}
      icon={Back}
      iconWeight="bold"
      variant={onImage ? "glass" : "elevated"}
      tone={onImage ? "neutral" : "primary"}
      size="icon-sm"
      iconSize="md"
      label={label}
      onClick={onClick}
      className={cn("pointer-events-auto", className)}
    />
  );
}
